// Fixed age group range: 10–17 individually, 18 represents "18+"
export const AGE_GROUPS = [
    ...Array.from({ length: 8 }, (_, i) => {
        const age = i + 10; // 10..17
        return { age, displayAge: String(age) + " years" };
    }),
    { age: 18, displayAge: '18+' },
];
export function calculateAge(birthYear) {
    return new Date().getFullYear() - parseInt(birthYear);
}
export function getAgeGroup(birthYear) {
    if (!birthYear)
        return AGE_GROUPS[0] ?? null;
    const calculated = calculateAge(birthYear);
    const clamped = Math.min(Math.max(calculated, AGE_GROUPS[0].age), AGE_GROUPS[AGE_GROUPS.length - 1].age);
    return AGE_GROUPS.find(a => a.age === clamped) ?? null;
}
/**
 * Rankings age range. Wider at the bottom than AGE_GROUPS, which starts at 10 because the
 * Club Leaderboard clamps to it. swimmingresults.org does publish age 9, and a 9 year old
 * needs to be able to build a 9-10 group.
 */
export const RANKING_AGE_MIN = 9;
export const RANKING_AGE_MAX = 18;
/**
 * Ages a combined group may span. Every extra age is a whole extra page fetch per level
 * event, so this is a request-volume bound rather than a rule of the sport.
 */
export const MAX_COMBINED_AGES = 3;
/** How far either side of their own age a swimmer may reach when building a group. */
const COMBINED_AGE_REACH = 2;
/**
 * Human label for a set of ages: "11", "10-11", "10-12", "9/11", "18+"
 * Contiguous runs collapse to a range so the common case reads exactly as it did when
 * 10-11 was hardcoded.
 */
export function formatAgeGroupLabel(ages) {
    const sorted = [...new Set(ages)].sort((a, b) => a - b);
    if (sorted.length === 0)
        return '';
    if (sorted.length === 1)
        return sorted[0] === RANKING_AGE_MAX ? '18+' : String(sorted[0]);
    const contiguous = sorted.every((age, i) => i === 0 || age === sorted[i - 1] + 1);
    return contiguous ? `${sorted[0]}-${sorted[sorted.length - 1]}` : sorted.join('/');
}
/** Ages offered in the combined-group editor: the swimmer's own age plus two either side. */
export function rankingAgeOptions(ownAge) {
    const from = Math.max(RANKING_AGE_MIN, ownAge - COMBINED_AGE_REACH);
    const to = Math.min(RANKING_AGE_MAX, ownAge + COMBINED_AGE_REACH);
    const options = [];
    for (let age = from; age <= to; age++) {
        options.push({ age, displayAge: formatAgeGroupLabel([age]) });
    }
    return options;
}
/**
 * A saved group is only meaningful while it still contains the swimmer's ranking year, and
 * that year increments every January. Resolve lazily on read rather than migrating storage.
 * An aged-out group is simply ignored until the swimmer picks a new one.
 */
export function resolveCombinedAgeGroup(saved, ownAge) {
    if (!saved || saved.length < 2)
        return undefined;
    const sorted = [...new Set(saved)].sort((a, b) => a - b);
    if (sorted.length < 2 || !sorted.includes(ownAge))
        return undefined;
    return sorted;
}
